// src/oura/newscan/fixtures/search.ts
//
// Search Console in the fixtures: each sample has a few invented searches,
// each tied to the word values a question must be filled with to ask it. A
// question whose fill_values hold all of them shows the count; a search no
// question asks becomes a suggestion (a gsc_candidate decision) when it names
// the word edit that would ask it. Not connected: no counts at all, never 0.

import type { ScanDraftSearchHit } from "@service/aeo/scanDraftTypes";
import type { FixtureSearch } from "./samples";

export function searchMatches(fillValues: Record<string, string>, search: FixtureSearch): boolean {
    const entries = Object.entries(search.match);
    // A text-only search (brand rows) never matches on words.
    if (!entries.length) return false;
    return entries.every(([variable, value]) => (fillValues[variable] ?? "").toLowerCase() === value.toLowerCase());
}

/** Brand rows have no words: a search matches when the question's text holds its words (search_basis "text"). */
export function searchMatchesText(text: string, search: FixtureSearch): boolean {
    if (!search.matchText) return false;
    const norm = (s: string) => s.toLowerCase().replace(/[?？,!]/g, "").replace(/\s+/g, " ").trim();
    return norm(text).includes(norm(search.matchText));
}

/** The searches a brand question answers, matched on its text. Null when it answers none. */
export function rowSearchHitByText(text: string, searches: FixtureSearch[]): ScanDraftSearchHit | null {
    return hitOf(searches.filter((s) => searchMatchesText(text, s)));
}

/** The searches one question answers, summed, best first. Null when it answers none. */
export function rowSearchHit(fillValues: Record<string, string>, searches: FixtureSearch[]): ScanDraftSearchHit | null {
    return hitOf(searches.filter((s) => searchMatches(fillValues, s)));
}

function hitOf(matched: FixtureSearch[]): ScanDraftSearchHit | null {
    const hits = [...matched].sort((a, b) => b.searches - a.searches);
    if (!hits.length) return null;
    const positions = hits.map((h) => h.position).filter((p): p is number => p !== null);
    return {
        searches: hits.reduce((sum, h) => sum + h.searches, 0),
        clicks: hits.reduce((sum, h) => sum + h.clicks, 0),
        position: positions.length ? Math.min(...positions) : null,
        queries: hits.map((h) => ({ query: h.query, searches: h.searches })),
    };
}

/** "2,400" style, for decision titles. */
export function formatSearches(n: number): string {
    return n.toLocaleString("en-US");
}
